import type { GuideMeta } from './types';
import { GENERATED_GUIDES } from './generated';

/**
 * Guide metadata registry.
 *
 * Metadata lives here — separate from the article bodies — so the homepage,
 * hub, sitemap and structured data can read it without pulling three large
 * article bundles into the initial chunk. Bodies are lazy-loaded by slug in
 * `bodies.ts`.
 *
 * MAINTENANCE CONTRACT: `lastVerifiedAt` is a promise to the reader and to
 * Google. Do not bump it unless someone actually re-checked the figures against
 * the primary sources listed on the guide.
 */

const EDITORIAL_DISCLAIMER_MONEY =
  'This guide is general information, not financial or tax advice. Fees and exchange rates change frequently and vary by amount, funding method, and destination bank. Confirm current pricing with the provider before you send, and consult a qualified tax professional about your own reporting obligations.';

const EDITORIAL_DISCLAIMER_VISA =
  'This guide is general information, not legal advice. Immigration rules change and are applied at the discretion of the reviewing officer. Always confirm current requirements with the Korea Immigration Service or your nearest Korean consulate before filing, and consider retaining a licensed administrative scrivener (행정사) for complex cases.';

/** Hand-authored flagship guides. Bodies live in sibling .tsx files. */
export const HANDWRITTEN_GUIDES: GuideMeta[] = [
  /* ── 1. Financial & Remittance ────────────────────────────── */
  {
    slug: 'sending-money-to-korea',
    category: 'financial',
    title:
      'The Complete Guide to Sending Money to Korea: Wire Fees, Exchange Rates & Tax Rules',
    titleKo: '한국 송금 완벽 가이드: 수수료, 환율, 세금 규정',
    shortTitle: 'Sending Money to Korea',
    description:
      'Compare transfer fees, exchange-rate markups, and delivery speed for sending USD to Korea — plus the IRS and NTS reporting thresholds that actually apply to you.',
    excerpt:
      'Most people lose more money to the exchange-rate markup than to the visible fee. Here is how the real cost breaks down across specialist transfer services, digital banks, and traditional wires — and what the IRS and Korea’s NTS expect you to report.',
    readingTimeMinutes: 14,
    publishedAt: '2026-08-09',
    updatedAt: '2026-08-09',
    lastVerifiedAt: '2026-08-09',
    authorId: 'tim-park',
    factChecked: true,
    gradient: 'from-[#0047AB] via-[#003A8A] to-[#1C1C1C]',
    keywords: [
      'send money to Korea',
      'USD to KRW transfer',
      'WireBarley vs Remitly',
      'Korea remittance fees',
      'wire transfer to Korean bank',
      'FBAR Korea',
      'Korea gift tax remittance',
    ],
    disclaimer: EDITORIAL_DISCLAIMER_MONEY,
    toc: [
      { id: 'true-cost', label: 'The two costs of a transfer', labelKo: '송금의 두 가지 비용' },
      { id: 'comparison', label: 'Provider comparison', labelKo: '업체 비교' },
      { id: 'speed', label: 'How fast money actually arrives', labelKo: '실제 도착 속도' },
      { id: 'tax-us', label: 'US reporting: IRS & FinCEN', labelKo: '미국 신고 의무' },
      { id: 'tax-korea', label: 'Korean reporting: NTS & gift tax', labelKo: '한국 신고 의무' },
      { id: 'choosing', label: 'Which service fits your situation', labelKo: '상황별 추천' },
      { id: 'mistakes', label: 'Five expensive mistakes', labelKo: '자주 하는 실수' },
      { id: 'faq', label: 'FAQ', labelKo: '자주 묻는 질문' },
    ],
    faq: [
      {
        question: 'What is the cheapest way to send money to Korea?',
        answer:
          'For amounts under about $5,000, specialist remittance services aimed at the Korean corridor — such as WireBarley, Remitly, or Wise — are almost always cheaper than a bank wire, because they apply a small or zero fixed fee and a much narrower exchange-rate margin. A traditional bank wire typically costs $25–$50 in outbound fees plus a 2–4% exchange-rate markup, and the receiving Korean bank may deduct an additional handling charge. Always compare the total KRW amount the recipient will actually receive, not the advertised fee.',
      },
      {
        question: 'How much money can I send to Korea without reporting it?',
        answer:
          'There is no US limit on how much you may legally send. Reporting is separate from permission. Korean banks report inbound foreign-currency receipts above USD 10,000 to the National Tax Service through the Bank of Korea foreign exchange network, and US persons with foreign accounts totalling more than $10,000 at any point in the year must file an FBAR (FinCEN Form 114). Sending your own money to your own Korean account is not taxable, but it may be reportable.',
      },
      {
        question: 'Do I have to pay tax on money I send to my parents in Korea?',
        answer:
          'Money you send to your own account in Korea is a transfer of your own funds and is not income. Money you give to another person, including a parent, can be a gift for Korean gift-tax purposes, and in Korea the recipient is the taxpayer. A Korean-resident parent receiving a gift from an adult child can apply a deduction of KRW 50 million per 10-year period for gifts between lineal relatives; amounts above that are taxable to them. Confirm your specific situation with a Korean tax accountant (세무사).',
      },
      {
        question: 'Why is the exchange rate I was quoted different from Google?',
        answer:
          'Google shows the mid-market rate — the midpoint between the buy and sell price on the interbank market. Almost no consumer service gives you that rate. The difference between the mid-market rate and the rate you are offered is the exchange-rate margin, and it is the largest hidden cost in most transfers. On a $3,000 transfer, a 2.5% margin costs $75, which usually dwarfs the visible fee.',
      },
      {
        question: 'How long does a transfer to a Korean bank take?',
        answer:
          'Specialist services frequently deliver to major Korean banks within minutes to a few hours during Korean banking hours, because they hold local KRW liquidity and pay out domestically. A SWIFT bank wire typically takes one to three business days and can take longer if it routes through a correspondent bank or arrives outside Korean business hours. Transfers initiated on a Friday US time often land the following Monday in Korea.',
      },
    ],
    sources: [
      {
        label: 'FBAR — Report of Foreign Bank and Financial Accounts',
        url: 'https://www.irs.gov/businesses/small-businesses-self-employed/report-of-foreign-bank-and-financial-accounts-fbar',
        publisher: 'Internal Revenue Service',
      },
      {
        label: 'Form 3520 — Reporting large gifts from foreign persons',
        url: 'https://www.irs.gov/businesses/gifts-from-foreign-person',
        publisher: 'Internal Revenue Service',
      },
      {
        label: 'National Tax Service — English tax information portal',
        url: 'https://www.nts.go.kr/english/',
        publisher: 'National Tax Service (국세청)',
      },
      {
        label: 'Foreign exchange transaction regulations',
        url: 'https://www.bok.or.kr/eng/main/main.do',
        publisher: 'Bank of Korea (한국은행)',
      },
    ],
    related: ['f4-visa-guide', 'finding-bilingual-mentors'],
  },

  /* ── 2. Visa & Legal ──────────────────────────────────────── */
  {
    slug: 'f4-visa-guide',
    category: 'visa',
    title: 'Moving to Korea: The F-4 Visa Application Guide for the Diaspora',
    titleKo: '한국으로: 재외동포(F-4) 비자 신청 가이드',
    shortTitle: 'F-4 Visa Guide',
    description:
      'Who qualifies for Korea’s F-4 Overseas Korean visa, the full document checklist, what it does and does not let you do, and how to convert it into a residence card.',
    excerpt:
      'The F-4 is the single most useful visa available to the Korean diaspora — multi-year, renewable, and close to unrestricted. The hard part is proving your lineage on paper. This is the eligibility test, the document checklist, and the order to do things in.',
    readingTimeMinutes: 16,
    publishedAt: '2026-08-09',
    updatedAt: '2026-08-09',
    lastVerifiedAt: '2026-08-09',
    authorId: 'tim-park',
    factChecked: true,
    gradient: 'from-[#C8102E] via-[#A00D25] to-[#1C1C1C]',
    keywords: [
      'F-4 visa Korea',
      '재외동포 비자',
      'overseas Korean visa',
      'F-4 visa requirements',
      'F-4 visa documents',
      'Korean heritage visa',
      'F-4 alien registration card',
    ],
    disclaimer: EDITORIAL_DISCLAIMER_VISA,
    toc: [
      { id: 'what-is-f4', label: 'What the F-4 actually is', labelKo: 'F-4 비자란' },
      { id: 'eligibility', label: 'Do you qualify?', labelKo: '자격 요건' },
      { id: 'restrictions', label: 'What the F-4 does not allow', labelKo: '제한 사항' },
      { id: 'documents', label: 'Document checklist', labelKo: '필요 서류' },
      { id: 'proving-lineage', label: 'Proving lineage: the hard part', labelKo: '가족관계 증명' },
      { id: 'steps', label: 'Application, step by step', labelKo: '신청 절차' },
      { id: 'after-arrival', label: 'After you arrive', labelKo: '입국 후 절차' },
      { id: 'military', label: 'Military service and dual nationality', labelKo: '병역 · 이중국적' },
      { id: 'faq', label: 'FAQ', labelKo: '자주 묻는 질문' },
    ],
    faq: [
      {
        question: 'Who is eligible for the F-4 Overseas Korean visa?',
        answer:
          'Two groups qualify. First, people who once held Republic of Korea nationality themselves and later acquired a foreign nationality. Second, their direct descendants — children and grandchildren of someone who held Korean nationality — who hold foreign citizenship and reside abroad. You must be a foreign national; the F-4 is not available to current Korean citizens, and applicants must clear criminal-record and, where applicable, military-service checks.',
      },
      {
        question: 'Can I work in Korea on an F-4 visa?',
        answer:
          'Yes, with limits. The F-4 permits most professional and business activity without a separate work permit or employer sponsorship, which is what makes it so valuable. It has historically excluded certain categories of simple manual labour and a small list of restricted occupations, though those employment restrictions have been progressively relaxed. Verify the current restricted-occupation list with Korea Immigration before accepting a role in construction, manufacturing line work, or hospitality service.',
      },
      {
        question: 'How long is the F-4 visa valid?',
        answer:
          'The F-4 is typically issued with a validity period of up to three years and is renewable, with each renewal generally granting up to a further three years of stay. Renewal is routine for holders who maintain a clean record. There is no cap on the number of renewals, and long-term F-4 holders can pursue F-5 permanent residency once they meet its separate requirements.',
      },
      {
        question: 'What is the hardest part of the F-4 application?',
        answer:
          'Documenting the chain of relationships between you and the ancestor who held Korean nationality. You need Korean-side records — a family relationship certificate (가족관계증명서), and for older cases an extract from the pre-2008 family register (제적등본) — plus foreign-side records such as birth and marriage certificates connecting each generation. If your Korean ancestor emigrated before 1960 or if a name was transliterated inconsistently, expect to order archival records and to have foreign documents apostilled and translated.',
      },
      {
        question: 'Do I need to apply from outside Korea?',
        answer:
          'The standard route is to apply at the Korean embassy or consulate with jurisdiction over your place of residence abroad, since the F-4 requires that you reside overseas. Some applicants already lawfully in Korea on another status can apply to change status at a local immigration office, but eligibility for in-country change depends on your current visa type. Check with the office that has jurisdiction over your Korean address before assuming you can convert in-country.',
      },
    ],
    sources: [
      {
        label: 'Visa navigator and F-series visa requirements',
        url: 'https://www.visa.go.kr/openPage.do?MENU_ID=10101',
        publisher: 'Korea Visa Portal, Ministry of Justice',
      },
      {
        label: 'Hi Korea — immigration procedures and Alien Registration',
        url: 'https://www.hikorea.go.kr/Main.pt',
        publisher: 'Korea Immigration Service',
      },
      {
        label: 'Act on the Immigration and Legal Status of Overseas Koreans',
        url: 'https://www.law.go.kr/LSW/eng/engMain.do',
        publisher: 'Korea Ministry of Government Legislation',
      },
      {
        label: 'Family relationship certificate issuance (전자가족관계등록시스템)',
        url: 'https://efamily.scourt.go.kr/',
        publisher: 'Supreme Court of Korea',
      },
    ],
    related: ['sending-money-to-korea', 'finding-bilingual-mentors'],
  },

  /* ── 3. Expat Life & Culture ──────────────────────────────── */
  {
    slug: 'finding-bilingual-mentors',
    category: 'life',
    title: 'How to Find English & Bilingual Mentors in Major Global Cities',
    titleKo: '주요 도시에서 이중언어 멘토 찾는 방법',
    shortTitle: 'Finding Bilingual Mentors',
    description:
      'A city-by-city directory of Korean professional networks, alumni chapters, and government programmes — plus how to ask for mentorship in a way people say yes to.',
    excerpt:
      'Mentorship is the highest-leverage thing a young Korean professional abroad can find, and the hardest to ask for. Here is where the bilingual networks actually are in seven cities, and the outreach script that gets replies.',
    readingTimeMinutes: 13,
    publishedAt: '2026-08-09',
    updatedAt: '2026-08-09',
    lastVerifiedAt: '2026-08-09',
    authorId: 'tim-park',
    factChecked: true,
    gradient: 'from-[#FFB81C] via-[#E09900] to-[#1C1C1C]',
    keywords: [
      'Korean mentorship abroad',
      'bilingual mentor',
      'Korean professional network',
      'KSEA',
      'Korean American professionals',
      'career mentor Korean',
    ],
    toc: [
      { id: 'why-hard', label: 'Why this is hard to ask for', labelKo: '멘토 요청이 어려운 이유' },
      { id: 'four-sources', label: 'The four places mentors come from', labelKo: '멘토를 찾는 네 곳' },
      { id: 'directory', label: 'City directory', labelKo: '도시별 디렉토리' },
      { id: 'outreach', label: 'The outreach message that works', labelKo: '연락 방법' },
      { id: 'first-meeting', label: 'Running the first conversation', labelKo: '첫 만남' },
      { id: 'cultural', label: 'Korean vs Western mentorship norms', labelKo: '문화 차이' },
      { id: 'faq', label: 'FAQ', labelKo: '자주 묻는 질문' },
    ],
    faq: [
      {
        question: 'How do I find a Korean-speaking mentor in my field?',
        answer:
          'Work outward through four concentric circles: your university’s Korean alumni chapter, profession-specific Korean associations such as KSEA for scientists and engineers or KALAGNY for lawyers in New York, your employer’s Asian or Korean employee resource group, and finally city-level Korean professional meetups. Alumni chapters have the highest response rate because the shared institution gives a stranger a reason to reply.',
      },
      {
        question: 'What should my first message to a potential mentor say?',
        answer:
          'Keep it under 150 words and make it easy to say yes. Name the specific connection between you, state one concrete question you want their view on, ask for a single 20-minute call rather than open-ended mentorship, and offer two time windows. Vague requests for "guidance" get ignored because they ask the reader to design the relationship for you.',
      },
      {
        question: 'Should I write to a Korean mentor in Korean or English?',
        answer:
          'Match the language of the context you found them in. If you found them through a Korean-language association page or their profile is in Korean, open in Korean using formal 존댓말 and honorifics; if you found them through an English-language professional network, open in English. When in doubt, write the first message in both, English first and a short Korean paragraph beneath. Signalling that you can operate in both languages is itself the point.',
      },
      {
        question: 'Is it rude to approach a senior Korean professional directly?',
        answer:
          'It is not rude, but seniority conventions are stronger in Korean professional culture than in most Western ones. Use full titles rather than first names, keep the first message formal, be explicit about the small size of your ask, and do not follow up more than once. An introduction from a mutual contact carries substantially more weight than a cold message, so spend your effort finding that intermediary first.',
      },
    ],
    sources: [
      {
        label: 'Korean-American Scientists and Engineers Association (KSEA)',
        url: 'https://www.ksea.org/',
        publisher: 'KSEA',
      },
      {
        label: 'Overseas Koreans Agency — diaspora support programmes',
        url: 'https://www.oka.go.kr/',
        publisher: 'Overseas Koreans Agency (재외동포청)',
      },
      {
        label: 'Korean Cultural Centers worldwide',
        url: 'https://www.kocis.go.kr/eng/openInformation.do',
        publisher: 'Korean Culture and Information Service',
      },
    ],
    related: ['f4-visa-guide', 'sending-money-to-korea'],
  },
];

/**
 * Every guide the site serves: hand-authored plus PUBLISHED generated ones.
 *
 * Drafts are filtered out inside `generated.ts`, so a draft can never acquire a
 * route, a prerendered page, or a sitemap entry by accident. Everything
 * downstream — routing, the hub, prerender, sitemap — reads this one array,
 * which is what keeps the route list from drifting as guides are added.
 */
export const GUIDES: GuideMeta[] = [...HANDWRITTEN_GUIDES, ...GENERATED_GUIDES];

export const GUIDES_BY_SLUG: Record<string, GuideMeta> = Object.fromEntries(
  GUIDES.map((g) => [g.slug, g])
);

export function getGuide(slug: string | undefined): GuideMeta | undefined {
  return slug ? GUIDES_BY_SLUG[slug] : undefined;
}

export function guidesByCategory(category: string): GuideMeta[] {
  return GUIDES.filter((g) => g.category === category);
}
